import { useCallback } from 'react';

import type { Reference } from '@apollo/client';
import { gql } from '@apollo/client';
import { uniqBy } from 'lodash';
import { Apollo } from 'src/gql/generated';

export function createFolderSyncJobsForConnectionKey(integration: Apollo.FileStorageIntegration) {
  const jobKey: keyof Apollo.Query = 'folderSyncJobsForConnection';
  return `${jobKey}({"integration":"${integration}"})`;
}

export function useBulkCreateFolderSyncJobs(integration: Apollo.FileStorageIntegration) {
  const [bulkCreateFolderSyncJobs, { loading }] = Apollo.useBulkCreateFolderSyncJobsMutation();
  const safeBulkCreateFolderSyncJobs = useCallback(
    async (options: Apollo.BulkCreateFolderSyncJobsMutationOptions) => {
      // Options should include variables from caller
      await bulkCreateFolderSyncJobs({
        ...options,
        update: (cache, { data }) => {
          const folderSyncJobsKey: keyof Apollo.Query = 'folderSyncJobs';
          const createdJobs = data?.bulkCreateFolderSyncJobs.createdJobs ?? [];
          const deletedFolderSyncJobIds = new Set(
            data?.bulkCreateFolderSyncJobs.deletedFolderSyncJobIds ?? []
          );
          const createdJobRefs = createdJobs.flatMap(job => {
            const ref = cache.writeFragment({
              data: job,
              fragment: gql`
                fragment NewFolderSyncJob on FolderSyncJob {
                  __typename
                  id
                }
              `,
            });
            return ref ? [ref] : [];
          });

          cache.modify({
            fields: {
              [folderSyncJobsKey]: (existingJobs: readonly Reference[] = [], { readField }) => {
                const filteredJobRefs = existingJobs.filter(job => {
                  const jobId = readField<UUID>('id', job);
                  return jobId && !deletedFolderSyncJobIds.has(jobId);
                });
                return uniqBy([...filteredJobRefs, ...createdJobRefs], ref =>
                  readField<UUID>('id', ref)
                );
              },
              [createFolderSyncJobsForConnectionKey(integration)]: (
                existingJobs: readonly Reference[] = [],
                { readField }
              ) => {
                const filteredJobRefs = existingJobs.filter(job => {
                  const jobId = readField<UUID>('id', job);
                  return jobId && !deletedFolderSyncJobIds.has(jobId);
                });
                return uniqBy([...filteredJobRefs, ...createdJobRefs], ref =>
                  readField<UUID>('id', ref)
                );
              },
            },
          });
        },
        variables: {
          input: options?.variables?.input ?? { driveSyncs: [], integration },
        },
      });
    },
    [bulkCreateFolderSyncJobs, integration]
  );

  return {
    bulkCreateFolderSyncJobs: safeBulkCreateFolderSyncJobs,
    loading,
  };
}

export function useDeleteFolderSyncJobs() {
  const [deleteFolderSyncJobs] = Apollo.useDeleteFolderSyncJobsMutation();
  const safeDeleteFolderSyncJobs = useCallback(
    async (ids: string[]) => {
      await deleteFolderSyncJobs({
        optimisticResponse: {
          __typename: 'Mutation',
          deleteFolderSyncJobs: ids,
        },
        update: (cache, { data }) => {
          if (!data?.deleteFolderSyncJobs) return;

          const deletedIds = new Set(data.deleteFolderSyncJobs);
          cache.modify({
            fields: {
              folderSyncJobs: (existingJobs: readonly Reference[] = [], { readField }) => {
                return (existingJobs as Reference[]).filter((job: Reference) => {
                  const jobId = readField<UUID>('id', job);
                  return jobId && !deletedIds.has(jobId);
                });
              },
              folderSyncJobsForConnection: (existingJobs = [], { readField }) => {
                return (existingJobs as Reference[]).filter((job: Reference) => {
                  const jobId = readField<UUID>('id', job);
                  return jobId && !deletedIds.has(jobId);
                });
              },
            },
          });
        },
        variables: { ids },
      });
    },
    [deleteFolderSyncJobs]
  );

  return {
    deleteFolderSyncJobs: safeDeleteFolderSyncJobs,
  };
}
